import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import EmailEditor from 'react-email-editor';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import { useUIStore } from '../../stores/uiStore';
import * as contractTemplatesApi from '../../api/contractTemplates';

/**
 * Template Editor Page
 * WYSIWYG editor for creating/editing contract templates
 */
export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const emailEditorRef = useRef(null);
  const { showToast } = useUIStore();

  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [editorKey, setEditorKey] = useState(0); // Force editor remount when this changes
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    template_html: '',
    template_design: null,
    template_variables: [],
    signature_mode: 'zones', // Default to 'zones' for backward compatibility
  });

  // Default contract template variables (fallback for new templates)
  const defaultVariables = [
    { name: 'creation_date', label: 'Creation Date', type: 'date', required: true },
    { name: 'trainer_name', label: 'Trainer Name', type: 'text', required: true },
    { name: 'trainer_email', label: 'Trainer Email', type: 'email', required: false },
    { name: 'trainer_iban', label: 'Trainer IBAN', type: 'text', required: false },
    { name: 'trainer_tennaamstelling', label: 'Tennaamstelling', type: 'text', required: false },
    { name: 'season_start', label: 'Season Start Year', type: 'number', required: true },
    { name: 'season_end', label: 'Season End Year', type: 'number', required: true },
    { name: 'month_start', label: 'Start Month', type: 'text', required: true },
    { name: 'month_end', label: 'End Month', type: 'text', required: true },
    { name: 'team_name', label: 'Team Name', type: 'text', required: true },
    { name: 'total_amount', label: 'Total Amount', type: 'number', required: true },
    { name: 'installments', label: 'Number of Installments', type: 'number', required: true },
    { name: 'payment_schedule', label: 'Payment Schedule', type: 'text', required: true },
    { name: 'iban', label: 'IBAN', type: 'text', required: true },
    { name: 'email', label: 'Email Address', type: 'email', required: true },
    { name: 'birth_date', label: 'Birth Date', type: 'date', required: true },
    { name: 'tc_name', label: 'TC Name', type: 'text', required: true },
    { name: 'tc_digital_sign', label: 'TC Digital Signature Text', type: 'text', required: false },
    { name: 'tc_user_signature', label: 'Creator Signature Image', type: 'text', required: false },
    { name: 'trainer_signature', label: 'Trainer Signature Image (Variable Mode)', type: 'auto_trainer_signature_placeholder', required: false },
    { name: 'trainer_digital_sign', label: 'Trainer Digital Signature Text', type: 'text', required: false },
    { name: 'location', label: 'Location', type: 'text', required: true },
    { name: 'sign_date', label: 'Sign Date', type: 'date', required: true },
  ];

  // Use template variables if loaded, otherwise use defaults
  const activeVariables = template.variables && template.variables.length > 0
    ? template.variables
    : defaultVariables;

  useEffect(() => {
    if (id) {
      loadTemplate();
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      console.log('Loading template with ID:', id);
      
      const data = await contractTemplatesApi.getTemplate(id);
      console.log('Template data received:', {
        id: data.id,
        name: data.name,
        hasHtml: !!data.template_html,
        hasDesign: !!data.template_design,
        designLength: data.template_design?.length,
        variablesCount: data.variables?.length
      });
      
      // Set template with variables array (already parsed by backend)
      // Merge database variables with defaults to ensure all variables exist
      const dbVariables = data.variables || data.template_variables || [];
      
      // Create a map of existing variables by name
      const existingVarMap = new Map(dbVariables.map(v => [v.name, v]));
      
      // Merge: keep all DB variables, add missing defaults
      const mergedVariables = [...defaultVariables];
      mergedVariables.forEach((defaultVar, index) => {
        if (existingVarMap.has(defaultVar.name)) {
          // Use DB version if it exists
          mergedVariables[index] = existingVarMap.get(defaultVar.name);
        }
      });
      
      // Add any DB variables that aren't in defaults (custom variables)
      dbVariables.forEach(dbVar => {
        if (!mergedVariables.find(v => v.name === dbVar.name)) {
          mergedVariables.push(dbVar);
        }
      });
      
      console.log('Template variables merged:', {
        dbCount: dbVariables.length,
        defaultsCount: defaultVariables.length,
        mergedCount: mergedVariables.length,
        dbVars: dbVariables.map(v => v.name),
        mergedVars: mergedVariables.map(v => v.name)
      });
      
      setTemplate({
        ...data,
        variables: mergedVariables,
        signature_mode: data.signature_mode || 'zones' // Default to zones if not set
      });
      
      // Force editor to remount with new merge tags
      setEditorKey(prev => prev + 1);
      setEditorReady(false); // Reset ready state for new editor instance
    } catch (error) {
      showToast('Failed to load template', 'error');
      console.error('Error loading template:', error);
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  // Compute editor config dynamically - this creates a NEW object each render
  // so React will see it as changed and re-initialize the editor with new merge tags
  // FIXED: Always use defaultVariables to ensure ALL 21 variables appear in autocomplete
  const editorConfig = {
    displayMode: 'web',
    tools: {
      image: { enabled: true },
      text: { enabled: true },
      button: { enabled: true },
      divider: { enabled: true },
      html: { enabled: true },
      heading: { enabled: true },
      social: { enabled: false },
      menu: { enabled: false },
      timer: { enabled: false },
      video: { enabled: false },
    },
    appearance: {
      theme: 'light',
      panels: {
        tools: {
          dock: 'left',
        },
      },
    },
    // Enable merge tags with special tags syntax
    features: {
      textEditor: {
        spellChecker: true,
        tables: true,
        emojis: true,
      },
    },
    // FIX: Use defaultVariables directly instead of activeVariables
    // This ensures all 22 variables (including trainer_signature) are ALWAYS available in autocomplete
    mergeTags: defaultVariables.reduce((acc, v) => {
      acc[v.name] = {
        name: v.label,
        value: `{${v.name}}`,
        sample: v.label,
      };
      return acc;
    }, {}),
    mergeTagsConfig: {
      autocompleteTriggerChar: '{',
      sort: false,
      delimiter: ['{', '}'],
    },
  };

  const onEditorReady = () => {
    const mergeTagsArray = Array.isArray(editorConfig.mergeTags)
      ? editorConfig.mergeTags
      : Object.values(editorConfig.mergeTags);
    console.log('Editor ready with merge tags:', mergeTagsArray.length);
    console.log('Merge tags:', mergeTagsArray.map(t => t.value));
    console.log('Expected 23 variables, got:', mergeTagsArray.length);
    if (mergeTagsArray.length !== 23) {
      console.warn('WARNING: Not all merge tags are loaded!');
      console.warn('Missing variables:', defaultVariables.filter(dv =>
        !mergeTagsArray.find(mt => mt.value === `{${dv.name}}`)
      ).map(v => v.name));
    }
    setEditorReady(true);
  };

  // Load design into editor when both editor is ready AND template data is loaded
  useEffect(() => {
    if (!editorReady || !id || !emailEditorRef.current) {
      return;
    }

    console.log('Attempting to load design. Template state:', {
      hasDesign: !!template.template_design,
      designLength: template.template_design?.length
    });

    if (template.template_design) {
      try {
        console.log('Loading template design for template ID:', id);
        
        // Parse template_design (not template_html!)
        const design = typeof template.template_design === 'string'
          ? JSON.parse(template.template_design)
          : template.template_design;

        console.log('Parsed design object:', design);

        // Validate design structure
        if (design && design.body) {
          console.log('Loading design into editor...');
          emailEditorRef.current.editor.loadDesign(design);
          console.log('Design loaded successfully');
        } else {
          console.warn('Invalid design structure - missing body property');
          showToast('Template design is invalid', 'warning');
        }
      } catch (error) {
        console.error('Failed to parse template design:', error);
        console.error('Template design content:', template.template_design);
        showToast('Failed to load template design', 'error');
      }
    } else {
      console.warn('Template exists but has no design data');
      showToast('This template has no design data', 'warning');
    }
  }, [editorReady, template.template_design, id]);

  const handleSave = async () => {
    if (!template.name.trim()) {
      showToast('Please enter a template name', 'error');
      return;
    }

    if (!emailEditorRef.current) {
      showToast('Editor not ready', 'error');
      return;
    }

    setSaving(true);

    try {
      // Export HTML and design from editor
      emailEditorRef.current.editor.exportHtml(async (data) => {
        const { design, html } = data;

        console.log('Exporting template:', {
          hasDesign: !!design,
          hasHtml: !!html,
          designKeys: Object.keys(design || {}),
          htmlLength: html?.length
        });

        try {
          const templateData = {
            name: template.name,
            description: template.description,
            template_html: html,
            template_design: JSON.stringify(design), // Save design JSON
            template_variables: JSON.stringify(defaultVariables), // Stringify like design
            signature_mode: template.signature_mode || 'zones', // Include signature mode
          };

          console.log('Saving template data:', {
            name: templateData.name,
            designStringLength: templateData.template_design.length,
            htmlLength: templateData.template_html.length,
            variablesCount: templateData.template_variables.length
          });

          let savedTemplate;
          if (id) {
            savedTemplate = await contractTemplatesApi.updateTemplate(id, templateData);
            console.log('Template updated:', savedTemplate.id);
          } else {
            savedTemplate = await contractTemplatesApi.createTemplate(templateData);
            console.log('Template created:', savedTemplate.id);
          }
          
          showToast('Template saved successfully', 'success');
          navigate('/templates');
        } catch (error) {
          showToast('Failed to save template', 'error');
          console.error('Error saving template:', error);
          console.error('Error details:', error.response?.data);
        } finally {
          setSaving(false);
        }
      });
    } catch (error) {
      showToast('Failed to save template', 'error');
      console.error('Error in save process:', error);
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.editor.exportHtml((data) => {
      const { html } = data;

      // Replace variables with sample data
      const sampleData = {
        creation_date: new Date().toLocaleDateString('nl-NL'),
        trainer_name: 'John Doe',
        trainer_email: 'trainer@example.com',
        trainer_iban: 'NL00 BANK 0123 4567 89',
        trainer_tennaamstelling: 'T.n.v. John Doe',
        season_start: '2024',
        season_end: '2025',
        month_start: 'september',
        month_end: 'juni',
        team_name: 'Meiden C3/B4',
        total_amount: '375',
        installments: '2',
        payment_schedule: 'halverwege en aan het einde van het seizoen',
        iban: 'NL00 BANK 0123 4567 89',
        email: 'trainer@example.com',
        birth_date: '01-01-1990',
        tc_name: 'Jane Smith',
        tc_digital_sign: `Digitaal ondertekend door ondergetekende op ${new Date().toLocaleDateString('nl-NL')} vanaf IP: 192.168.1.1`,
        tc_user_signature: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        trainer_signature: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        trainer_digital_sign: `Digitaal ondertekend door trainer op ${new Date().toLocaleDateString('nl-NL')} vanaf IP: 192.168.1.2`,
        location: 'Veenendaal',
        sign_date: new Date().toLocaleDateString('nl-NL'),
      };

      let previewHtml = html;
      Object.keys(sampleData).forEach(key => {
        const regex = new RegExp(`\\{${key}\\}`, 'g');
        let value = sampleData[key];
        
        // Check if value is a base64 image data URL
        if (typeof value === 'string' && value.startsWith('data:image/')) {
          // Convert to HTML img tag for proper rendering
          value = `<img src="${value}" alt="Signature" style="max-width: 200px; max-height: 80px; display: inline-block;" />`;
        }
        
        previewHtml = previewHtml.replace(regex, value);
      });

      const previewWindow = window.open('', 'Contract Preview', 'width=800,height=900');
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/templates')}
          className="flex-shrink-0"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? 'Edit Template' : 'Create Template'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Design your contract template using the visual editor
          </p>
        </div>
      </div>

      {/* Template Info */}
      <Card>
        <div className="space-y-4">
          <Input
            label="Template Name"
            value={template.name}
            onChange={(e) => setTemplate({ ...template, name: e.target.value })}
            placeholder="e.g., Standard Trainer Contract"
            required
          />
          <Input
            label="Description"
            value={template.description}
            onChange={(e) => setTemplate({ ...template, description: e.target.value })}
            placeholder="Brief description of this template"
          />
          
          {/* Signature Mode Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trainer Signature Mode
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="signature_mode"
                  value="zones"
                  checked={template.signature_mode === 'zones'}
                  onChange={(e) => setTemplate({ ...template, signature_mode: e.target.value })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Zone-Based (Legacy)</div>
                  <div className="text-sm text-gray-500">
                    Fixed XY coordinates - use Signature Zone Editor after saving
                  </div>
                </div>
              </label>
              
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="signature_mode"
                  value="variable"
                  checked={template.signature_mode === 'variable'}
                  onChange={(e) => setTemplate({ ...template, signature_mode: e.target.value })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Variable-Based (Recommended)</div>
                  <div className="text-sm text-gray-500">
                    Use <code className="bg-gray-200 px-1 rounded">{'{trainer_signature}'}</code> variable - flows naturally with content
                  </div>
                </div>
              </label>
            </div>
            
            {template.signature_mode === 'variable' && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Insert the <code className="bg-blue-200 px-1 rounded">{'{trainer_signature}'}</code> variable
                  in your template where you want the trainer's signature to appear. It will flow naturally with your content,
                  solving positioning issues with dynamic payment schedules.
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Available Variables */}
      <Card className="bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-3">Available Variables:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {activeVariables.map((variable) => (
            <span
              key={variable.name}
              className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-blue-100 text-blue-800"
              title={variable.label}
            >
              {`{${variable.name}}`}
            </span>
          ))}
        </div>
        <p className="text-xs text-blue-700 mt-3">
          Use these variables in your template. They will be replaced with actual values when generating contracts.
          Click "Merge Tags" in the editor toolbar to insert variables.
        </p>
      </Card>

      {/* Visual Editor */}
      <Card>
        <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '600px' }}>
          <EmailEditor
            key={editorKey}
            ref={emailEditorRef}
            onReady={onEditorReady}
            options={editorConfig}
            minHeight="600px"
          />
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <Button
          variant="secondary"
          onClick={handlePreview}
          disabled={!editorReady}
        >
          Preview with Sample Data
        </Button>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/templates')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={!editorReady}
          >
            Save Template
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <Card className="bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Template Creation Tips:</h3>
        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
          <li>Use the drag-and-drop editor to design your contract layout</li>
          <li>Insert variables using the "Merge Tags" button in the toolbar</li>
          <li>Variables are wrapped in curly braces: {`{variable_name}`}</li>
          <li>Use <code className="bg-gray-200 px-1 rounded">{`{tc_user_signature}`}</code> and <code className="bg-gray-200 px-1 rounded">{`{tc_digital_sign}`}</code> for creator signature</li>
          <li>Use <code className="bg-gray-200 px-1 rounded">{`{trainer_signature}`}</code> and <code className="bg-gray-200 px-1 rounded">{`{trainer_digital_sign}`}</code> for trainer signature</li>
          <li>Use "Preview with Sample Data" to see how the contract will look</li>
          <li>Zone-based mode: Configure trainer signature zones in Signature Zone Editor after saving</li>
          <li>Variable-based mode: Insert {`{trainer_signature}`} variable directly in your template</li>
        </ul>
      </Card>
    </div>
  );
}